import React from 'react';
import { Domain, DomainDisabled } from '@material-ui/icons';
import PropTypes from 'prop-types';
import Empty from './Empty';
import { makeStyles } from '@material-ui/core';

/**
 * @typedef {import('src/lib/type').CompanySearchHit} CompanySearchHit
 */

const useStyles = makeStyles((theme) => ({
  companiesSection: {
    flex: 1,
    [theme.breakpoints.up('md')]: {
      overflowY: 'auto',
    },
    borderBottomRightRadius: theme.shape.borderRadius,
    [theme.breakpoints.down('sm')]: {
      '&.empty-suggestions': {
        display: 'none',
      },
    },
    backgroundColor: '#F9FBFF',
    borderTopRightRadius: theme.shape.borderRadius,
  },
  sectionHeader: {
    position: 'sticky',
    top: 0,
    padding: '12px 16px',
    borderBottom: '1px solid #e5e7eb',
    zIndex: 10,
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#1f2937',
    margin: 0,
  },
  sectionCount: {
    fontSize: '14px',
    color: '#6b7280',
    fontWeight: 'normal',
  },
  companiesList: {
    padding: '8px 12px',
    [theme.breakpoints.up('md')]: {
      overflowY: 'auto',
      maxHeight: '600px',
    },
  },
  companyItem: {
    width: '100%',
    padding: '16px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    backgroundColor: '#fff',
    border: '1px solid rgba(229, 231, 235, 0.5)',
    borderRadius: '8px',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'all 0.2s ease',
    margin: '8px 0',
    boxShadow: '0 0 5px 0 rgba(152, 149, 149, 0.16)',
    '&:hover': {
      backgroundColor: 'rgba(59, 130, 246, 0.05)',
      borderColor: 'rgba(59, 130, 246, 0.3)',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    },
    '& em': {
      fontStyle: 'normal',
      color: theme.palette.primary.main,
    },
    '&.preferred': {
      borderColor: 'rgba(59, 130, 246, 0.3)',
    },
  },
  companyImageContainer: {
    width: '60px',
    height: '60px',
    borderRadius: '8px',
    overflow: 'hidden',
    flexShrink: 0,
    backgroundColor: '#f3f4f6',
    border: '1px solid #e5e7eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(0.5),
  },
  companyImage: {
    width: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
  },
  companyPlaceholder: {
    color: '#9ca3af',
    fontSize: '32px',
    fontWeight: 'normal',
  },
  companyContent: {
    flex: 1,
    textAlign: 'left',
    minWidth: 0,
  },
  companyTitle: {
    fontWeight: '600',
    fontSize: '18px',
    color: '#1f2937',
    marginBottom: '4px',
    transition: 'color 0.2s ease',
  },
  companyHierarchy: {
    fontSize: '12px',
    color: '#6b7280',
    marginBottom: '8px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  companyDescription: {
    fontSize: '14px',
    color: '#6b7280',
    lineHeight: '1.5',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  companyMeta: {
    marginTop: '8px',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    fontSize: '12px',
  },
  companyWebsite: {
    color: 'rgba(59, 130, 246, 0.7)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '200px',
  },
  badges: {
    backgroundColor: 'rgba(73, 163, 218, 0.09)',
    padding: '4px 9px',
    color: '#49a3da',
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    flexShrink: 0,
  },
  companyEmail: {
    color: '#6b7280',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '200px',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    marginBottom: theme.spacing(0.5),
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
      gap: '2px',
    },
  },
}));

/**
 * Component for displaying company results
 * @param {Object} props - The component properties
 * @param {CompanySearchHit[]} props.suggestions - The company suggestions to display
 * @param {function} props.onItemClick - Function called when clicking on an element
 * @returns {JSX.Element|null} The companies component
 */
export default function CompanyResults({ suggestions, onItemClick }) {
  const classes = useStyles();
  const hasSuggestions = suggestions && suggestions.length > 0;
  return (
    <div
      className={`${classes.companiesSection} ${
        !hasSuggestions ? 'empty-suggestions' : ''
      }`}
    >
      <div className={classes.sectionHeader}>
        <h3 className={classes.sectionTitle}>
          <Domain style={{ fontSize: '20px' }} />
          Companies
          <span className={classes.sectionCount}>({suggestions.length})</span>
        </h3>
      </div>
      <div className={classes.companiesList}>
        <CompanySuggestions
          suggestions={suggestions}
          onItemClick={onItemClick}
          hasSuggestions={hasSuggestions}
        />
      </div>
    </div>
  );
}

/**
 * Component for displaying company results
 * @param {Object} props - The component properties
 * @param {CompanySearchHit[]} props.suggestions - The company suggestions to display
 * @param {boolean} hasSuggestions - Indicates if there are suggestions to display
 * @param {function} props.onItemClick - Function called when clicking on an element
 * @returns {JSX.Element} The companies component
 */
function CompanySuggestions({ suggestions, onItemClick, hasSuggestions }) {
  const classes = useStyles();
  if (!hasSuggestions) {
    return (
      <Empty Icon={DomainDisabled}>
        <p>No company results found.</p>
      </Empty>
    );
  }

  return (
    <>
      {suggestions.map((suggestion) => (
        <button
          key={suggestion._id}
          className={`${classes.companyItem} ${
            !!suggestion.subscription ? 'preferred' : ''
          }`}
          onClick={() => onItemClick(suggestion)}
        >
          {/* Company Image or Icon */}
          <div className={classes.companyImageContainer}>
            {suggestion.imageUrl ? (
              <img
                src={suggestion.imageUrl}
                alt={suggestion.title}
                className={classes.companyImage}
                loading='lazy'
              />
            ) : (
              <div className={classes.companyPlaceholder}>
                <Domain style={{ fontSize: '32px' }} />
              </div>
            )}
          </div>

          {/* Company Content */}
          <article className={classes.companyContent}>
            <header className={classes.titleRow}>
              <div
                className={classes.companyTitle}
                dangerouslySetInnerHTML={{ __html: suggestion._formatted.title }}
              />
              {!!suggestion.subscription && (
                <span className={classes.badges}>Preferred supplier</span>
              )}
            </header>

            {suggestion.searchField && (
              <p
                className={classes.companyDescription}
                dangerouslySetInnerHTML={{ __html: suggestion._formatted.searchField }}
              />
            )}
          </article>
        </button>
      ))}
    </>
  );
}

CompanyResults.propTypes = {
  /** The company suggestions to display */
  suggestions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['company']).isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string,
      imageUrl: PropTypes.string,
    })
  ),
  /** Function called when clicking on an element */
  onItemClick: PropTypes.func,
};

CompanyResults.defaultProps = {
  suggestions: [],
  onItemClick: () => {},
};
